import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/DataTable/Columns/DataTableColumnHeader';
import { Text } from '@/components/Typography/Text';
import { type Preset, useFormatDate } from '@/hooks/date';
import { cn } from '@/lib/cn';
import { isDate } from '@/utils/typeGuards';

import type { HelperOptions } from '../types';

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : never;
    }[keyof T]
  : never;

/** Date,DateTimeを表示するためのカラム */
export function simpleDateColumn<TData>(
  id: NestedKeys<TData> & string,
  preset: keyof Preset = 'date',
  options: HelperOptions & {
    /** Text が活用されておらず独自のスタイルを当てていたため、デフォルトではその互換性を保つ。true を指定すると通常の Text のスタイルとなる。 */
    useDefaultText?: boolean;
    accessorKey?: string;
  },
): ColumnDef<TData> {
  const {
    i18nKey = 'createdOn',
    minWidth = '200px',
    sortable = false,
    useDefaultText = false,
    enableHiding = true,
    accessorKey,
  } = options;
  return {
    id: id,
    accessorKey: accessorKey ?? id,
    meta: { i18nKey, thStyle: { minWidth } },
    enableSorting: sortable,
    enableHiding,
    header(props) {
      return <DataTableColumnHeader {...props} />;
    },
    cell: function Cell({ row, getValue }) {
      const formatDate = useFormatDate();
      const value = getValue<string | Date | null>();
      const isSubRow = row.depth > 0;

      // 日付値のバリデーション
      const date =
        value instanceof Date ? value : value ? new Date(value) : null;
      const isValidDate = isDate(date);

      return (
        <Text
          className={cn(
            useDefaultText
              ? null
              : isSubRow
                ? 'text-xs text-neutral-muted-on-fill'
                : 'text-sm text-neutral-on-fill',
          )}
        >
          {isValidDate ? formatDate(date, { preset }) : '–'}
        </Text>
      );
    },
  };
}
